import React, { useRef, useState } from 'react';
import { ResearchSummary } from '../../types/study';
import { researchFigureTheme as theme } from '../../theme/researchFigureTheme';

const COPY = { download: 'Descargar SVG', caption: 'Figura con escalas y datos incluidos en el SVG. Desplaza horizontalmente para verla a tamaño legible.', frequency: 'Frecuencia (n)', empty: 'Sin respuestas' };

type Props = { summary: ResearchSummary; unit: string; studyTitle: string };
const colors = theme.series;
const dash = (index: number) => { return index % 2 ? '6 3' : undefined; };
const number = (value: number | null) => value == null ? '—' : Number(value.toFixed(2)).toString();
// Wrap even unbroken condition names so every exported label remains inside its row.
function wrap(value: string, limit = 88): string[] {
  const words = value.match(new RegExp(`.{1,${limit}}(?:\\s|$)|.{1,${limit}}`, 'g'));
  return words?.map((word) => word.trim()) || [''];
}
function Figure({ title, summary, studyTitle, height, notes, children }: Props & {
  title: string; height: number; notes: string[]; children: React.ReactNode;
}) {
  const ref = useRef<SVGSVGElement>(null);
  const [status, setStatus] = useState('');
  const [failed, setFailed] = useState(false);
  const context = [studyTitle, `Corte: ${summary.generated_at} · Participantes: ${summary.total_participants} · Completaron: ${summary.completed_participants}`].flatMap((line) => wrap(line));
  const top = 56 + context.length * 19;
  const lines = notes.flatMap((line) => wrap(line));
  const fullHeight = top + height + lines.length * 19 + 32;
  const download = () => {
    try {
      if (!ref.current) return;
      const blob = new Blob([new XMLSerializer().serializeToString(ref.current)], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${title.toLowerCase().replace(/[^a-z0-9]+/gi, '-')}.svg`;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      setFailed(false); setStatus('Figura descargada correctamente.');
    } catch { setFailed(true); setStatus('No pudimos preparar la figura. Inténtalo nuevamente.'); }
  };
  return <figure className="rd-figure">
    <div className="rd-figure__header"><h3>{title}</h3><button onClick={download} aria-label={`Descargar SVG: ${title}`}>{COPY.download}</button></div>
    <div className="rd-figure__scroll" role="region" aria-label={`${title}: figura desplazable`} tabIndex={0}>
      <svg ref={ref} xmlns="http://www.w3.org/2000/svg" width="720" height={fullHeight} viewBox={`0 0 720 ${fullHeight}`} role="img" aria-label={title} style={{ background: theme.paper, color: theme.ink, fontFamily: 'Arial, sans-serif', fontSize: 13 }} fill={theme.ink}>
        <title>{`${title} · ${studyTitle}`}</title><desc>{[...context, ...notes].join(' ')}</desc>
        <rect width="720" height={fullHeight} fill={theme.paper} />
        <text x="24" y="30" fontSize="20" fontWeight="bold">{title}</text>
        {context.map((line, i) => <text key={i} x="24" y={54 + i * 19}>{line}</text>)}
        <g transform={`translate(0 ${top})`}>{children}</g>
        {lines.map((line, i) => <text key={i} x="24" y={top + height + 16 + i * 19}>{line}</text>)}
      </svg>
    </div>
    <figcaption className="rd-figure__caption">{COPY.caption}</figcaption>
    {status && <p className="rd-figure__caption" role={failed ? 'alert' : 'status'}>{status}</p>}
  </figure>;
}
function Axis({ maximum, y, left = 220, right = 670, label, integer = false }: { maximum: number; y: number; left?: number; right?: number; label: string; integer?: boolean }) {
  const ticks = Array.from(new Set(Array.from({ length: 6 }, (_, i) => integer ? Math.round(maximum * i / 5) : maximum * i / 5)));
  return <g><line x1={left} x2={right} y1={y} y2={y} stroke={theme.axis} />{ticks.map((tick) => {
    const x = left + tick / maximum * (right - left);
    return <g key={tick}><line x1={x} x2={x} y1={y} y2={y + 5} stroke={theme.axis} /><text x={x} y={y + 22} textAnchor="middle">{number(tick)}</text></g>;
  })}<text x={(left + right) / 2} y={y + 47} textAnchor="middle">{label}</text></g>;
}
export function DistributionChart(props: Props) {
  const { summary } = props;
  const rows = summary.distribution.map((item) => ({ ...item, labels: wrap(item.verb, 23) }));
  let bottom = 20;
  const positions = rows.map((row) => { const y = bottom; bottom += Math.max(52, row.labels.length * 18 + 16); return y; });
  const maximum = Math.max(1, ...rows.map((item) => item.count));
  return <Figure {...props} title="Distribución por condición" height={bottom + 65} notes={['Base: todas las participaciones asignadas, incluidas las incompletas.', ...rows.map((item) => `${item.verb}: n = ${item.count}; proporción = ${number(item.percentage)} %.`)]}>
    {rows.map((item, i) => <g key={item.condition}>{item.labels.map((line, j) => <text key={j} x="24" y={positions[i] + 16 + j * 18}>{line}</text>)}<rect x="220" y={positions[i]} height="22" width={item.count / maximum * 380} fill={colors[i % colors.length]} /><text x={228 + item.count / maximum * 380} y={positions[i] + 16}>{item.count}</text></g>)}
    <Axis maximum={maximum} y={bottom} right={600} label="Participantes (n)" integer />
  </Figure>;
}
export function FrequencyChart(props: Props) {
  const { summary, unit } = props;
  const values = summary.frequencies.flatMap((series) => series.values);
  const maximum = Math.max(1, ...values.map((item) => item.speed));
  const maxCount = Math.max(1, ...values.map((item) => item.count));
  const x = (value: number) => 80 + value / maximum * 590;
  const y = (value: number) => 220 - value / maxCount * 170;
  const ticks = Array.from(new Set(Array.from({ length: 6 }, (_, i) => Math.round(maxCount * i / 5))));
  const legend = summary.frequencies.flatMap((series, i) => wrap(`${i + 1}. ${series.verb}`, 80).map((line) => ({ line, i })));
  const notes = ['Base: respuestas completas. Frecuencias absolutas por velocidad; las líneas unen observaciones.', ...summary.frequencies.map((series) => `${series.verb}: ${series.values.map((item) => `${number(item.speed)} ${unit}: n = ${item.count}`).join('; ') || 'sin respuestas'}.`)];
  // Dense overlays can conceal entire series with identical frequencies. Keep
  // common scales, but give each condition its own directly labeled panel.
  if (summary.frequencies.length > colors.length) {
    let bottom = 36;
    const panels = summary.frequencies.map((series, i) => {
      const labels = wrap(`${i + 1}. ${series.verb}`, 80);
      const top = bottom;
      const plotTop = top + labels.length * 18 + 10;
      const baseline = plotTop + 100;
      bottom = baseline + 48;
      return { series, labels, top, plotTop, baseline };
    });
    return <Figure {...props} title="Frecuencias de velocidad" height={bottom + 35} notes={['Paneles por condición con escalas comunes.', ...notes]}>
      <text x="24" y="20">{COPY.frequency}</text>
      {panels.map(({ series, labels, top, plotTop, baseline }, i) => {
        const panelY = (count: number) => baseline - count / maxCount * 100;
        return <g key={series.condition} data-condition-panel={series.condition}>
          {labels.map((line, j) => <text key={j} x="80" y={top + j * 18} fontWeight="bold">{line}</text>)}
          {ticks.map((tick) => <g key={tick}><line x1="80" x2="670" y1={panelY(tick)} y2={panelY(tick)} stroke={theme.grid} /><text x="68" y={panelY(tick) + 4} textAnchor="end">{tick}</text></g>)}
          <line x1="80" x2="80" y1={plotTop} y2={baseline} stroke={theme.axis} />
          <Axis left={80} maximum={maximum} y={baseline} label="" />
          <polyline points={series.values.map((item) => `${x(item.speed)},${panelY(item.count)}`).join(' ')} fill="none" stroke={colors[i % colors.length]} strokeWidth="2" />
          {series.values.map((item) => <circle key={item.speed} cx={x(item.speed)} cy={panelY(item.count)} r="4" fill={colors[i % colors.length]} />)}
          {!series.values.length && <text x="100" y={plotTop + 35}>{COPY.empty}</text>}
        </g>;
      })}
      <text x="375" y={bottom + 10} textAnchor="middle">{`Velocidad estimada (${unit})`}</text>
    </Figure>;
  }
  return <Figure {...props} title="Frecuencias de velocidad" height={300 + legend.length * 20} notes={notes}>
    <text x="24" y="20">{COPY.frequency}</text>
    {ticks.map((tick) => <g key={tick}><line x1="80" x2="670" y1={y(tick)} y2={y(tick)} stroke={theme.grid} /><text x="68" y={y(tick) + 4} textAnchor="end">{tick}</text></g>)}
    <Axis left={80} maximum={maximum} y={220} label={`Velocidad estimada (${unit})`} />
    {summary.frequencies.map((series, i) => <g key={series.condition}><polyline points={series.values.map((item) => `${x(item.speed)},${y(item.count)}`).join(' ')} fill="none" stroke={colors[i % colors.length]} strokeWidth="2" strokeDasharray={dash(i)} />{series.values.map((item) => <g key={item.speed}><circle cx={x(item.speed)} cy={y(item.count)} r={4} fill={colors[i % colors.length]} /><title>{`${series.verb}: ${item.speed} ${unit}, n = ${item.count}`}</title></g>)}</g>)}
    {legend.map(({ line, i }, index) => <g key={index}><line x1="80" x2="110" y1={285 + index * 20} y2={285 + index * 20} stroke={colors[i % colors.length]} strokeWidth="3" strokeDasharray={dash(i)} /><text x="120" y={290 + index * 20}>{line}</text></g>)}
  </Figure>;
}
export function BoxPlotChart(props: Props) {
  const { summary, unit } = props;
  const maximum = Math.max(1, ...summary.boxplot.map((item) => item.maximum || 0));
  const x = (value: number) => 220 + value / maximum * 450;
  let bottom = 24;
  const rows = summary.boxplot.map((item) => { const labels = wrap(`${item.verb} (n = ${item.n})`, 23); const y = bottom; bottom += Math.max(60, labels.length * 18 + 16); return { ...item, labels, y }; });
  return <Figure {...props} title="Diagrama de caja" height={bottom + 65} notes={[
    `Velocidad estimada (${unit}). Base: respuestas completas. Caja: Q1–Q3; línea: mediana; bigotes: mínimo–máximo (no 1,5 RIC).`,
    ...rows.map((item) => `${item.verb}: n = ${item.n}; mínimo ${number(item.minimum)}, Q1 ${number(item.q1)}, mediana ${number(item.median)}, Q3 ${number(item.q3)}, máximo ${number(item.maximum)} ${unit}.`),
  ]}>
    {rows.map((item, i) => <g key={item.condition}>{item.labels.map((line, j) => <text key={j} x="24" y={item.y + 5 + j * 18}>{line}</text>)}
      {[item.minimum, item.q1, item.median, item.q3, item.maximum].some((value) => { return value == null; }) ? <text x="220" y={item.y + 5}>{COPY.empty}</text> : <g stroke={colors[i % colors.length]} strokeWidth="2">
        <line x1={x(item.minimum!)} x2={x(item.maximum!)} y1={item.y} y2={item.y} />
        {[item.minimum!, item.maximum!].map((value, j) => <line key={j} x1={x(value)} x2={x(value)} y1={item.y - 10} y2={item.y + 10} />)}
        <rect x={x(item.q1!)} y={item.y - 14} width={Math.max(1, x(item.q3!) - x(item.q1!))} height="28" fill={theme.paper} />
        <line x1={x(item.median!)} x2={x(item.median!)} y1={item.y - 14} y2={item.y + 14} strokeWidth="3" />
      </g>}
    </g>)}<Axis maximum={maximum} y={bottom} label={`Velocidad estimada (${unit})`} />
  </Figure>;
}
